import { flow } from 'fp-ts/function';
import { drop, filter, map, reduce, take } from 'lodash/fp';
import hash from 'object-hash';
import { Component, createElement, createRef } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { FinancialDocumentPdfTemplateType } from '@mews-commander/data-access';
import { ActionType, TextKey } from '@mews-commander/enums';
import { BreakpointEnumItem, BreakpointRange, DialogProvider, WithBreakpointRange } from '@mews-commander/ui';
import { LocalizedText } from '@mews/framework';
import { createHandler } from '@mews/std';
import { getSpacingToken, IconName, withBreakpointRange } from '@optimus-web/core';
import { HIDDEN_PRINT } from '../../../../components/commander-ui';
import { reportError } from '../../../../core/logger/actions';
import ActionDropdown from '../Common/ActionDropdown';
import * as ActionTemplates from './Action';
import { HeadingAction } from './Action/types';
import HandleIconButton from './Handles/HandleIconButton';
import HandleMenuItem from './Handles/HandleMenuItem';

const getVisibleActionsCount = createHandler(
    {
        [BreakpointRange.Phone.name]: 1,
        [BreakpointRange.Tablet.name]: 2,
    },
    4
);

const handleActionsVisibility = (actions: $TSFixMe, breakpointRange?: BreakpointEnumItem) => {
    const visibleActionsCount = getVisibleActionsCount(breakpointRange?.name);
    return actions.length - visibleActionsCount === 1
        ? [actions, []]
        : [take(visibleActionsCount, actions), drop(visibleActionsCount, actions)];
};

// Prefer using action id instead of hashing because hashing can be a heavier operation.
const getActionKey = (action: $TSFixMe) =>
    action.actionId ?? hash(action, { ignoreUnknown: true, excludeKeys: (key) => typeof action[key] !== 'string' });

export const ACTION_MENU_PORTAL_ID = 'action-menu-portal';

interface DispatchProps {
    reportError: typeof reportError;
}
interface OwnActionMenuProps extends WithBreakpointRange, DispatchProps {
    actions?: HeadingAction[];
    ghost?: boolean;
}

interface ActionMenuState {
    opened: boolean;
    showPrintReasonDialog: boolean;
    firstUsedTemplateType: FinancialDocumentPdfTemplateType | null;
}

type ActionMenuProps = OwnActionMenuProps;

class ActionMenu extends Component<ActionMenuProps, ActionMenuState> {
    opened: $TSFixMe;
    overlay: $TSFixMe;

    state = {
        opened: false,
        showPrintReasonDialog: false,
        firstUsedTemplateType: null,
    };

    getOverlayRef = (c: $TSFixMe) => (this.overlay = c);

    setShowPrintReasonDialog = (showPrintReasonDialog: boolean) => this.setState({ showPrintReasonDialog });

    setFirstUsedTemplateType = (firstUsedTemplateType: FinancialDocumentPdfTemplateType) =>
        this.setState({ firstUsedTemplateType });

    createAction = ({ action, key, component }: $TSFixMe) => {
        const props = {
            action,
            key,
            component,
        };

        if (action.type === ActionType.PrintFinancialDocument) {
            return this.createActionWithPrintReasonProps(ActionTemplates.PrintFinancialDocumentAction, {
                ...props,
                firstUsedTemplateType: this.state.firstUsedTemplateType,
                setFirstUsedTemplateType: this.setFirstUsedTemplateType,
            });
        }
        if (action.type === ActionType.Print) {
            return this.createActionWithPrintReasonProps(ActionTemplates.PrintAction, props);
        }
        if (action.type === ActionType.SendBillToCustomer) {
            return this.createActionWithPrintReasonProps(ActionTemplates.SendBillToCustomerAction, props);
        }
        if (action.type === ActionType.SendFinancialDocumentToEmail) {
            return this.createActionWithPrintReasonProps(ActionTemplates.SendFinancialDocumentToEmailAction, props);
        }
        if (action.type === ActionType.SendEmailAndReminderForInvoiceToEmailAction) {
            return this.createActionWithPrintReasonProps(
                ActionTemplates.SendEmailAndReminderForInvoiceToEmailAction,
                props
            );
        }
        // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'typeof import("/home/...
        return createElement(ActionTemplates[action.type], props);
    };

    createActionWithPrintReasonProps = (action: $TSFixMe, props: $TSFixMe) =>
        createElement(action, {
            ...props,
            showPrintReasonDialog: this.state.showPrintReasonDialog,
            setShowPrintReasonDialog: this.setShowPrintReasonDialog,
        });

    handleToggleMenu = () => {
        this.setState((prevState) => ({ opened: !prevState.opened }));
    };

    ref = createRef();
    renderVisibleActions = ({ ghost }: { ghost?: boolean }) =>
        map((action) => (
            <WrapperDiv key={getActionKey(action)}>
                {this.createAction({
                    action,
                    component: (props: JSX.Element) => (
                        <HandleIconButton {...props} variant={ghost ? 'ghost' : undefined} />
                    ),
                })}
            </WrapperDiv>
        ));
    renderOverflowedActions = map((action) =>
        this.createAction({ action, key: getActionKey(action), component: HandleMenuItem })
    );

    // @ts-expect-error $TSFixMeError: 'renderActionComponents' implicitly has type 'any' because it does not have a type annotation and is referenced directly...
    renderActionComponents = reduce((acc, action) => {
        // @ts-expect-error $TSFixMeError: Property 'type' does not exist on type 'unknown'.
        const { type } = action;
        if (type === ActionType.Dropdown) {
            return [...acc, this.renderActionComponents((action as $TSFixMe).items)];
        }
        return acc;
    }, []);

    isActionAvailable = (action: HeadingAction) => {
        if (ActionTemplates[action.type as ActionType] == null) {
            this.props.reportError(`ActionMenu: Action type ${action.type} is not supported.`);
            return false;
        }
        return true;
    };

    render() {
        const { breakpointRange, actions, ghost } = this.props;
        const availableActions = filter(this.isActionAvailable, actions);
        const [visibleActions, overflowedActions] = handleActionsVisibility(availableActions, breakpointRange);
        const actionComponents = this.renderActionComponents(availableActions);
        return (
            // @ts-expect-error $TSFixMeError: No overload matches this call.
            <ActionsWrapper ref={this.ref}>
                <DialogProvider>
                    <InjectedActionsWrapper id={ACTION_MENU_PORTAL_ID} />
                    {this.renderVisibleActions({ ghost })(visibleActions)}
                    {overflowedActions.length > 0 && (
                        <WrapperDiv>
                            <ActionDropdown
                                componentRef={this.getOverlayRef}
                                icon={IconName.More}
                                title={<LocalizedText textKey={TextKey.More} />}
                                component={HandleIconButton}
                                onOpen={this.handleToggleMenu}
                            >
                                {this.renderOverflowedActions(overflowedActions)}
                            </ActionDropdown>
                        </WrapperDiv>
                    )}
                    {actionComponents}
                </DialogProvider>
            </ActionsWrapper>
        );
    }
}

const ActionsWrapper = styled.div`
    display: flex;
    align-items: center;

    ${HIDDEN_PRINT}
`;

const InjectedActionsWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: ${getSpacingToken('100')}px;
    margin-right: ${getSpacingToken('100')}px;
`;

const WrapperDiv = styled.div`
    height: 100%;
    display: flex;
    align-items: center;
`;

const connectActionMenu = connect<null, DispatchProps, OwnActionMenuProps>(null, {
    reportError,
});

// @ts-expect-error $TSFixMeError: No overload matches this call.
export default flow(withBreakpointRange, connectActionMenu)(ActionMenu);
